import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { BannerAlert } from '@magoosh/lib/alerts';
import { SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import useSearchQuery from '@magoosh/utils/use_search_query';
import { paths } from 'config/path_helpers';
import { MockTest } from 'types/mock_test';

import * as api from '@test_prep/app/api';
import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';
import ContextualLink from '@test_prep/app/components/contextual_link';

import PastTestResultCard from './components/past_test_result_card';
import TemplateSelectionForm from './components/template_selection_form';
import UpsellCard from '../components/upsell_card';
import LegacyPage from './legacy';
import { MockTestTemplate, SectionOption } from './types';

interface Props {
  currentTest: MockTest;
  entitlements: {
    limit: number;
    limitReached: number;
  };
  recentTests: MockTest[];
  recommendedTemplate: MockTestTemplate;
  sectionOptions: SectionOption[];
  templates: MockTestTemplate[];
  ui: {
    saleMessage: string;
    showUpsell: boolean;
  };
}

export default () => {
  const { branding } = useAppContext().data;
  if (branding.examName !== 'ACT' && branding.examName !== 'TOEFL') return <LegacyPage />;

  const history = useHistory();
  const location = useLocation();
  const q = useQueries();
  const sectionSlug = useSearchQuery()['section'] as string;
  const [currentTestEnded, setCurrentTestEnded] = useState<boolean>(false);
  const [isEndingTest, setIsEndingTest] = useState<boolean>(false);

  const isSectionTest = location.pathname === '/practice_tests/start_section';
  const breadcrumbs = [
    { name: 'Home', path: paths.dashboard() },
    { name: isSectionTest ? 'Section Test' : 'Practice Test' }
  ];

  const pagePath = isSectionTest ? paths.practiceTestsStartSection(sectionSlug) : paths.practiceTestsStart(sectionSlug);
  const pageQuery = q.pageQuery(pagePath, { keepPreviousData: true })
  const page = pageQuery.data as Props;

  const getSectionOption = () => {
    if (!page) return;

    return page.sectionOptions.find((sectionOption) => (
      sectionOption.slug === sectionSlug
    )) || page.sectionOptions[0];
  };
  const [selectedSectionOption, setSelectedSectionOption] = useState<SectionOption>(getSectionOption());

  useEffect(() => {
    const option = getSectionOption();

    if (option && option.slug !== sectionSlug) {
      handleSectionOptionChange(option);
    } else {
      setSelectedSectionOption(option);
    }
  }, [page, sectionSlug]);

  const endCurrentTest = () => {
    setIsEndingTest(true);

    api.finishMockTest(page.currentTest.id).then((mockTest) => {
      setCurrentTestEnded(true);
      setIsEndingTest(false);
    }).catch((error) => {
      setIsEndingTest(false);
    });
  };

  const handleSectionOptionChange = (sectionOption: SectionOption) => {
    if (isSectionTest) {
      history.replace(paths.practiceTestsStartSection(sectionOption.slug))
    } else {
      history.replace(paths.practiceTestsStart(sectionOption.slug))
    }
  };

  const resumeCurrentTestPath = () => {
    if (!page.currentTest) return;

    if (page.currentTest.currentPracticeSession) {
      return paths.practiceSession(page.currentTest.currentPracticeSession.id);
    } else {
      return paths.practiceTestStartSection(page.currentTest.id);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isSectionTest ? 'Section' : 'Practice'} Test - Magoosh {branding.examName}</title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs} linkComponent={ContextualLink} loading={!selectedSectionOption}>
        {({ loading }) => {
          if (loading) return;

          return (
            <div className="d-flex flex-column gap-m">
              <div className="d-flex flex-column gap-s">
                <h1 className="text-gray-darker u-margin-A-n">
                  <strong>{branding.examName} {isSectionTest ? 'Section' : 'Practice'} Test</strong>
                </h1>

                <div>
                  {isSectionTest ? 'Section' : 'Practice'} tests simulate the real exam experience and provide personalized feedback on your performance.
                </div>
              </div>

              {
                page.currentTest && !currentTestEnded && (
                  <CardLayout.Card
                    borderGradient
                    className="align-items-sm-center d-flex flex-column flex-sm-row gap-s gap-sm-m"
                    style={{ padding: '15px 24px' }}
                  >
                    <div>
                      You are in the middle of <strong>{page.currentTest.name}</strong>.
                    </div>

                    <div className="align-items-center d-flex flex-1 flex-row-reverse flex-sm-row gap-m justify-content-end">
                      <a
                        className="text-gray-light small text-hover-no-decoration text-no-decoration"
                        onClick={endCurrentTest}
                      >
                        {isEndingTest && <FontAwesomeIcon icon="spinner" className="fa-pulse" />} End Test
                      </a>

                      <SecondaryButton href={resumeCurrentTestPath()}>
                        Resume Test
                      </SecondaryButton>
                    </div>
                  </CardLayout.Card>
                )
              }

              <div className="d-flex flex-column flex-sm-row gap-m">
                <div className="d-flex flex-column gap-m" style={{ flex: 2 }}>
                  <div className="d-flex flex-column gap-s gap-lg-m">
                    <h3 className="flex-1 u-margin-A-n u-margin-T-s">
                      <strong>
                        {`Take a ${isSectionTest ? 'Section' : 'Practice'} Test`
                        }
                      </strong>
                    </h3>

                    <hr className="u-margin-A-n" />
                  </div>

                  {
                    page.entitlements.limitReached && (
                      <BannerAlert>
                        You've reached the limit for practice tests and sections with your current plan. To get full access <ContextualLink href={paths.plans()}>upgrade your plan</ContextualLink>.
                      </BannerAlert>
                    )
                  }

                  <CardLayout.Card>
                    <TemplateSelectionForm
                      currentTest={page.currentTest}
                      isFetching={pageQuery.isFetching}
                      onSectionOptionChange={handleSectionOptionChange}
                      recommendedTemplate={page.recommendedTemplate}
                      sectionOptions={page.sectionOptions}
                      selectedSectionOption={selectedSectionOption}
                      templates={page.templates}
                    />
                  </CardLayout.Card>
                </div>

                <div className="d-flex flex-column gap-m" style={{ flex: 1 }}>
                  {
                    page.ui.showUpsell && (
                      <>
                        <div className="d-flex flex-column gap-s gap-lg-m">
                          <h3 className="flex-1 u-margin-A-n u-margin-T-s">
                            <strong>Get Premium</strong>
                          </h3>

                          <hr className="u-margin-A-n" />
                        </div>

                        <UpsellCard examName={branding.examName} saleMessage={page.ui.saleMessage} />
                      </>
                    )
                  }

                  <div className="d-flex flex-column gap-s gap-lg-m">
                    <div className="align-items-end d-flex">
                      <h3 className="flex-1 u-margin-A-n u-margin-T-s">
                        <strong>Recent Results</strong>
                      </h3>

                      <div>
                        <ContextualLink
                          className="small text-gray-light text-hover-no-decoration text-no-decoration"
                          href={paths.practiceTests()}
                        >
                          All Results
                        </ContextualLink>
                      </div>
                    </div>

                    <hr className="u-margin-A-n" />
                  </div>

                  {(() => {
                    if (pageQuery.isFetching) {
                      return (
                        <div className="d-flex justify-content-center">
                          <LoadingIndicator />
                        </div>
                      );
                    } else if (page.recentTests.length > 0) {
                      return (
                        <>
                          {
                            page.recentTests.map((pastTest, idx) => (
                              <PastTestResultCard
                                key={pastTest.id}
                                condensed={idx !== 0}
                                mockTest={pastTest}
                                selectedSectionOption={selectedSectionOption}
                              />
                            ))
                          }
                        </>
                      );
                    } else {
                      return (
                        <CardLayout.Card
                          className="small text-gray-light"
                          style={{ background: 'rgba(255, 255, 255, 0.66)' }}
                        >
                          No recent results to show.
                        </CardLayout.Card>
                      );
                    }
                  })()}
                </div>
              </div>
            </div>
          )
        }}
      </CardLayout>
    </>
  );
};
